import { TemplateGenerativeModel } from 'firebase/ai';
import { ProductDocument } from 'models/product';
import { useEffect, useEffectEvent, useReducer } from 'react';
import { UseFormReturn } from 'react-hook-form';

type AIFieldState = {
  status: 'idle' | 'updated' | 'loading';
  current: string;
  currentPlain?: string;
  previous?: string;
};

type AIFieldAction =
  | { type: 'START_LOADING' }
  | {
      type: 'UPDATE';
      payload: {
        new: string;
        newPlain?: string;
        previous: string;
      };
    }
  | { type: 'UNDO' }
  | { type: 'RESET'; payload: string };

function reducer(state: AIFieldState, action: AIFieldAction): AIFieldState {
  switch (action.type) {
    case 'START_LOADING':
      return { ...state, status: 'loading' };

    case 'UPDATE':
      return {
        status: 'updated',
        previous: action.payload.previous,
        current: action.payload.new,
        currentPlain: action.payload.newPlain,
      };

    case 'UNDO':
      if (state.status !== 'updated') return state;

      return {
        status: 'idle',
        current: state.previous ?? state.current,
        previous: undefined,
      };

    case 'RESET':
      return {
        status: 'idle',
        current: action.payload,
        previous: undefined,
      };

    default:
      return state;
  }
}

type Params = {
  form: UseFormReturn<ProductDocument>;
  product: ProductDocument;
  generativeModel: TemplateGenerativeModel;
  useRichText: boolean;
};

export const useProductAI = ({
  form,
  product,
  generativeModel,
  useRichText,
}: Params) => {
  const [titleState, dispatchTitle] = useReducer(reducer, {
    status: 'idle',
    current: form.getValues('title') || '',
  });

  const [descPlainState, dispatchDescPlain] = useReducer(reducer, {
    status: 'idle',
    current: form.getValues('description') || '',
  });

  const [descRichState, dispatchDescRich] = useReducer(reducer, {
    status: 'idle',
    current: form.getValues('description_html') || '',
  });

  const title = form.watch('title');
  const description = form.watch('description');
  const descriptionHtml = form.watch('description_html');

  const resetTitleState = useEffectEvent(() => {
    if (titleState.status === 'updated' && title !== titleState.current) {
      dispatchTitle({ type: 'RESET', payload: title });
    }
  });

  const resetDescPlainState = useEffectEvent(() => {
    if (
      descPlainState.status === 'updated' &&
      description !== descPlainState.current
    ) {
      dispatchDescPlain({ type: 'RESET', payload: description });
    }
  });

  const resetDescRichState = useEffectEvent(() => {
    if (
      descRichState.status === 'updated' &&
      description !== descRichState.currentPlain
    ) {
      dispatchDescRich({ type: 'RESET', payload: descriptionHtml || '' });
    }
  });

  useEffect(() => resetTitleState(), [title, titleState.status]);
  useEffect(() => resetDescPlainState(), [description, descPlainState.status]);
  useEffect(() => resetDescRichState(), [description, descRichState.status]);

  const enhanceTitle = async () => {
    if (titleState.status === 'loading') return;

    if (titleState.status === 'idle') {
      dispatchTitle({ type: 'START_LOADING' });

      try {
        const { response } = await generativeModel.generateContent(
          'title-enhancer',
          {
            description: product.description || null,
            rideTypes: [product.ride_types].flat().join(', ') || null,
            brand: product.brand || null,
            mpn: [product.mpn].flat().join(', ') || null,
            size: String(product.size) || null,
          }
        );

        const text = response.text().trim();
        if (!text) throw new Error('Empty response');

        dispatchTitle({
          type: 'UPDATE',
          payload: {
            new: text,
            previous: title,
          },
        });

        form.setValue('title', text);
      } catch {
        dispatchTitle({
          type: 'RESET',
          payload: form.getValues('title'),
        });
      }
    } else {
      dispatchTitle({ type: 'UNDO' });
      form.setValue('title', titleState.previous || '');
    }
  };

  const enhanceDescription = async () => {
    const dispatch = useRichText ? dispatchDescRich : dispatchDescPlain;
    const state = useRichText ? descRichState : descPlainState;
    const currentValue = useRichText ? descriptionHtml || '' : description;

    if (state.status === 'loading') return;

    if (state.status === 'idle') {
      dispatch({ type: 'START_LOADING' });

      try {
        const { response } = await generativeModel.generateContent(
          'description-enhancer',
          {
            title: product.title || null,
            rideTypes: [product.ride_types].flat().join(', ') || null,
            brand: product.brand || null,
            mpn: [product.mpn].flat().join(', ') || null,
            size: String(product.size) || null,
          }
        );

        const text = response.text().trim();
        if (!text) throw new Error('Empty response');

        dispatch({
          type: 'UPDATE',
          payload: {
            new: text,
            newPlain: text,
            previous: currentValue,
          },
        });

        if (useRichText) {
          form.setValue('description_html', text);
        } else {
          form.setValue('description', text);
        }
      } catch {
        dispatch({
          type: 'RESET',
          payload: useRichText
            ? form.getValues('description_html') || ''
            : form.getValues('description'),
        });
      }
    } else {
      dispatch({ type: 'UNDO' });
      const prev = state.previous || '';
      if (useRichText) {
        form.setValue('description_html', prev);
        form.setValue('description', prev);
      } else {
        form.setValue('description', prev);
      }
    }
  };

  const descState = useRichText ? descRichState : descPlainState;

  return {
    titleState,
    descState,
    enhanceTitle,
    enhanceDescription,
  };
};
